import { NgClass, NgIf, NgTemplateOutlet } from '@angular/common';
import type { OnInit, TemplateRef } from '@angular/core';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Input,
  inject,
  input,
} from '@angular/core';
import { outputFromObservable, takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import type { SizeType } from '@cf/core';
import type { DropdownPosition } from '@ng-select/ng-select';
import { NgSelectModule } from '@ng-select/ng-select';
import type { AddTagFn } from '@ng-select/ng-select/lib/ng-select.component';
import { NgxTolgeeModule } from '@tolgee/ngx';
import { NgxControlValueAccessor } from 'ngxtension/control-value-accessor';
import { Subject, debounceTime, distinctUntilChanged } from 'rxjs';
import type { DataProvider, EntityType } from '../providers';

@Component({
  selector: 'cf-select-new',
  imports: [NgSelectModule, FormsModule, NgIf, NgTemplateOutlet, NgClass, NgxTolgeeModule],
  templateUrl: './select-new.component.html',
  styleUrl: './select-new.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  hostDirectives: [NgxControlValueAccessor],
})
export class SelectNewComponent<Entity extends EntityType> implements OnInit {
  cva = inject(
    NgxControlValueAccessor<
      | (DataProvider<Entity>['config']['valueKey'] extends keyof Entity
          ? Entity[DataProvider<Entity>['config']['valueKey']]
          : Entity)
      | undefined
    >,
  );
  destroyRef = inject(DestroyRef);

  provider = input.required<DataProvider<Entity>>();
  placeholder = input('');
  appendTo = input('body');
  clearable = input(false);
  searchable = input(false);
  addTagText = input('');
  footerTemplate = input<TemplateRef<unknown>>();
  optionTemplate = input<TemplateRef<unknown>>();
  labelTemplate = input<TemplateRef<unknown>>();
  size = input<SizeType>();
  optionFullView = input(false);
  borderLess = input(false);
  disabled = input(false);
  dropdownPosition = input<DropdownPosition>('bottom');

  @Input() addTagFn: AddTagFn | boolean = false;

  selectChange = outputFromObservable(this.cva.valueChange);

  typeahead$ = new Subject<string>();

  ngOnInit() {
    this.typeahead$
      .pipe(distinctUntilChanged(), debounceTime(200), takeUntilDestroyed(this.destroyRef))
      .subscribe((value) => {
        this.provider().setSearch(value);
      });
  }

  valueChange(value: typeof this.cva.value) {
    this.cva.value$.set(value);
  }
}
